<template>
  <div class="reports">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">{{ $t("messages.reports") }}</h1>
        <p class="page-subtitle">{{ $t("messages.reports_subtitle") }}</p>
      </div>
    </div>

    <div class="content">
      <div v-if="campaigns.length === 0" class="empty-state">
        {{ $t("messages.reports_no_campaigns") }}
      </div>

      <div v-else class="report-card">
        <div class="report-section">
          <h2 class="report-section-title">{{ $t("messages.reports_campaign_section") }}</h2>

          <div class="form-group">
            <label class="form-label">{{ $t("messages.available_campaigns") }}</label>
            <select
              v-model="selectedCampaign"
              class="form-input campaign-select"
              @change="onCampaignChange"
            >
              <option :value="null">-- {{ $t("messages.select_campaigns_title") }} --</option>
              <option v-for="c in campaigns" :key="c.id" :value="c.id">
                {{ campaignLabel(c) }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t("messages.reports_ad_group_label") }}</label>
            <select
              v-model="selectedAdGroup"
              class="form-input campaign-select"
              :disabled="!selectedCampaign"
              @change="onAdGroupChange"
            >
              <option :value="null">{{ $t("messages.reports_all_ad_groups") }}</option>
              <option v-for="ag in campaignAdGroups" :key="ag.id" :value="ag.id">
                {{ adGroupLabel(ag) }}
              </option>
            </select>
          </div>
        </div>

        <div class="report-section">
          <h2 class="report-section-title">{{ $t("messages.reports_period_section") }}</h2>
          <p class="field-hint">{{ $t("messages.reports_date_hint") }}</p>

          <div class="date-fields">
            <div class="form-group">
              <label class="form-label">
                <img src="/images/calendar-days.svg" alt="" class="label-icon" />
                {{ $t("messages.start_date") }}
              </label>
              <input type="date" v-model="startDate" class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">
                <img src="/images/calendar-days.svg" alt="" class="label-icon" />
                {{ $t("messages.end_date") }}
              </label>
              <input type="date" v-model="endDate" class="form-input" />
            </div>
          </div>
        </div>

        <div v-if="currentCampaign" class="campaign-preview">
          <div class="campaign-preview-name">{{ campaignLabel(currentCampaign) }}</div>
          <div v-if="currentAdGroup" class="campaign-preview-adgroup">
            {{ adGroupLabel(currentAdGroup) }}
          </div>
          <div class="campaign-preview-meta">
            <span v-if="previewStartDate || previewEndDate" class="campaign-preview-period">
              {{ formatDate(previewStartDate) }} — {{ formatDate(previewEndDate) }}
            </span>
            <span
              v-if="previewStatus"
              class="status-badge"
              :class="getStatusClass(previewStatus)"
            >
              {{ getStatusText(previewStatus) }}
            </span>
            <span
              v-if="currentCampaign && currentCampaign.is_test"
              class="status-badge status-test"
            >
              {{ $t('messages.test_campaign_badge') }}
            </span>
          </div>
        </div>

        <div class="report-actions">
          <button
            class="btn-primary"
            :disabled="!selectedCampaign || !startDate || !endDate"
            @click="downloadReport"
          >
            <img src="/images/import.svg" alt="" />
            {{ $t("messages.download_report") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Reports",
  props: {
    campaigns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedCampaign: null,
      selectedAdGroup: null,
      startDate: null,
      endDate: null,
    };
  },
  computed: {
    currentCampaign() {
      return this.campaigns.find((c) => c.id === this.selectedCampaign) || null;
    },
    campaignAdGroups() {
      if (!this.currentCampaign) {
        return [];
      }
      return this.currentCampaign.ad_groups || this.currentCampaign.adGroups || [];
    },
    currentAdGroup() {
      if (!this.selectedAdGroup) {
        return null;
      }
      return this.campaignAdGroups.find((ag) => ag.id === this.selectedAdGroup) || null;
    },
    previewStartDate() {
      if (this.currentAdGroup?.start_date) {
        return this.currentAdGroup.start_date;
      }
      return this.currentCampaign?.start_date;
    },
    previewEndDate() {
      if (this.currentAdGroup?.end_date) {
        return this.currentAdGroup.end_date;
      }
      return this.currentCampaign?.end_date;
    },
    previewStatus() {
      return this.currentAdGroup?.status || this.currentCampaign?.status;
    },
  },
  methods: {
    campaignLabel(c) {
      const name = c.name || c.campaign || c.title || `#${c.id}`;
      return c.is_test ? `[${this.$t('messages.test_campaign_badge')}] ${name}` : name;
    },
    adGroupLabel(ag) {
      return ag.name || `#${ag.id}`;
    },
    onCampaignChange() {
      this.selectedAdGroup = null;
      this.applyDatesFromSelection();
    },
    onAdGroupChange() {
      this.applyDatesFromSelection();
    },
    applyDatesFromSelection() {
      const campaign = this.currentCampaign;
      if (!campaign) {
        this.startDate = null;
        this.endDate = null;
        return;
      }

      const source = this.currentAdGroup || campaign;
      this.startDate = this.toInputDate(source.start_date);
      this.endDate = this.toInputDate(source.end_date);
    },
    toInputDate(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      return d.toISOString().slice(0, 10);
    },
    formatDate(date) {
      if (!date) return "—";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("ru-RU");
    },
    getStatusClass(status) {
      switch (status) {
        case "active":
          return "status-active";
        case "pending":
          return "status-pending";
        case "completed":
          return "status-completed";
        case "paused":
          return "status-paused";
        default:
          return "status-default";
      }
    },
    getStatusText(status) {
      switch (status) {
        case "active":
          return this.$t("messages.active_status");
        case "paused":
          return this.$t("messages.paused_status");
        case "pending":
          return this.$t("messages.pending_status");
        case "completed":
          return this.$t("messages.completed_status");
        default:
          return status;
      }
    },
    downloadReport() {
      if (!this.selectedCampaign) {
        return;
      }

      if (!this.startDate || !this.endDate) {
        const msg = this.$t("messages.select_date_range") || "Please select start and end dates";
        if (this.$notify) {
          this.$notify({ title: this.$t("messages.download_report"), message: msg, type: "warning" });
        } else {
          alert(msg);
        }
        return;
      }

      if (this.startDate > this.endDate) {
        const msg = this.$t("messages.invalid_date_range") || "Start date must be before or equal to end date";
        if (this.$notify) {
          this.$notify({ title: this.$t("messages.download_report"), message: msg, type: "error" });
        } else {
          alert(msg);
        }
        return;
      }

      const start = this.startDate;
      const end = this.endDate;
      let url = `/campaigns/${this.selectedCampaign}/download-statistic/All/${start}/${end}`;

      if (this.selectedAdGroup) {
        url += `?ad_group_id=${this.selectedAdGroup}`;
      }

      this.$emit("download-report", this.selectedCampaign);
      window.open(url, "_blank");

      const successMsg = this.$t("messages.download_report_started") || "Report download started";
      if (this.$notify) {
        this.$notify({ title: this.$t("messages.download_report"), message: successMsg, type: "success" });
      }
    },
  },
};
</script>
